// The Studio — two rental options presented as two products.
//
// Everything here already existed; almost none of it was doing any selling.
// The gear list was behind a "What's Included" accordion, the parking and
// transit details only reached you in the email sent AFTER you booked, and the
// only published number was $100/hr. This page puts the gear next to the price
// (at $100/hr in Westchester the number has to arrive with its justification)
// and puts getting-here near the top, because "five minutes from Metro-North,
// free parking after 7" is the sharpest answer to "why not book in the city".

import React from 'react';
import { Bay, Btn, Card, Defs, Eyebrow, Head, OtherDoor, PhotoStrip, Rates, Shell, Ticks } from '../primitives.jsx';
import { useSiteData, spacePhotos } from '../data.js';
import { go } from '../routes.js';
import { STUDIO } from '../content.js';

function GearBlock({ sections }) {
  if (!sections || !sections.length) return null;
  return (
    <div className="hddn-grid hddn-grid--2">
      {sections.map((s) => (
        <div key={s.heading}>
          <p className="hddn-label">{s.heading}</p>
          <div style={{ marginTop: 12 }}><Ticks items={s.items} /></div>
        </div>
      ))}
    </div>
  );
}

function AddOns({ items, title }) {
  if (!items || !items.length) return null;
  return (
    <div>
      <p className="hddn-label">{title}</p>
      <table className="hddn-rates" style={{ marginTop: 10 }}>
        <caption className="hddn-vis-hidden">{title}</caption>
        <tbody>
          {items.map((a) => (
            <tr key={a.id || a.label}>
              <th scope="row" style={{ fontFamily: 'var(--sans)', textTransform: 'none', letterSpacing: 0, fontSize: 'var(--step-0)', fontWeight: 400, color: 'var(--ink)' }}>
                {a.label}
                {a.note ? <span className="hddn-sub">{a.note}</span> : null}
                {a.options && a.options.length ? <span className="hddn-sub">{a.options.join(' · ')}</span> : null}
              </th>
              <td><span className="hddn-price">{a.price ? `$${a.price}` : '—'}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Specs render only when someone has actually filled them in. A fabricated
// square footage is a claim a renter can check with a tape measure.
function Specs({ specs }) {
  const rows = [
    specs.squareFeet && { t: 'Square footage', d: specs.squareFeet },
    specs.dimensions && { t: 'Dimensions', d: specs.dimensions },
    specs.ceilingHeight && { t: 'Ceiling height', d: specs.ceilingHeight },
    specs.naturalLight && { t: 'Natural light', d: specs.naturalLight },
  ].filter(Boolean);
  if (!rows.length) return null;
  return (
    <div style={{ marginTop: 'var(--gap)' }}>
      <p className="hddn-label">The room</p>
      <div style={{ marginTop: 10 }}><Defs items={rows} /></div>
    </div>
  );
}

export default function Studio() {
  const d = useSiteData();
  const { blockRates, studioGear, podcastPackages, podcastGear, studioAddOns, podcastAddOns, uses, gettingHere, goodToKnow, specs } = d;

  const fullPhotos = spacePhotos(d.photos, 'upstairs-1');
  const podPhotos = spacePhotos(d.photos, 'upstairs-2');

  return (
    <>
      <Bay band="ground">
        <Eyebrow>{STUDIO.street} · {STUDIO.city}</Eyebrow>
        <h1 className="hddn-h1" style={{ marginTop: 18, maxWidth: '15ch' }}>Book the room.</h1>
        <p className="hddn-lede" style={{ marginTop: 22, maxWidth: '50ch' }}>
          Two spaces behind a tunnel entrance in downtown New Rochelle: a full photo and video
          studio, and a dedicated podcast room. {STUDIO.hoursLabel}. Rates below, calendar open.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 30 }}>
          <Btn href="/book" onClick={go('/book')} variant="fill">Check availability</Btn>
          <Btn href="#full-studio" variant="line">Rates and gear</Btn>
        </div>
      </Bay>

      {/* Why here rather than the city. This used to be post-booking email copy. */}
      <Bay tight band="raised">
        <div className="hddn-grid hddn-grid--3">
          <div>
            <p className="hddn-label">Getting here</p>
            <ul style={{ marginTop: 12 }}>
              {gettingHere.transit.map((t) => (
                <li key={t} style={{ marginBottom: 8 }} className="hddn-muted">{t}</li>
              ))}
            </ul>
          </div>
          <div>
            <p className="hddn-label">Parking</p>
            <ul style={{ marginTop: 12 }}>
              {gettingHere.parking.map((p) => (
                <li key={p.lot} style={{ marginBottom: 8 }} className="hddn-muted">
                  <strong style={{ color: 'var(--ink)' }}>{p.lot}</strong> — {p.where}, {p.walk}
                  {p.note ? ` · ${p.note}` : ''}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="hddn-label">Worth knowing</p>
            <p className="hddn-muted" style={{ marginTop: 12 }}>{gettingHere.parkingNote}</p>
          </div>
        </div>
        <p className="hddn-note" style={{ marginTop: 22 }}>{gettingHere.intro}</p>
      </Bay>

      {/* ---- Option 1: Full Studio ---- */}
      <Bay id="full-studio">
        <Head
          eyebrow="Option one"
          title="Full Studio"
          lede="The space itself. Photo, video, content days, workshops, listening sessions, pop-ups, DJ sets."
        />
        {fullPhotos.length ? (
          <div style={{ marginBottom: 'var(--gap)' }}>
            <PhotoStrip photos={fullPhotos} alt="The Full Studio at HDDN LAB" ratio="wide" limit={8} />
          </div>
        ) : null}

        <div className="hddn-grid hddn-grid--sidebar">
          <div>
            <p className="hddn-label">What it is good for</p>
            <div style={{ marginTop: 12 }}><Ticks items={uses['full-studio']} /></div>
            <Specs specs={specs} />
          </div>
          <Card>
            <p className="hddn-label">Rates</p>
            <div style={{ marginTop: 12 }}>
              <Rates
                caption="Full Studio rates"
                rows={blockRates}
                foot={`Up to ${STUDIO.maxGuests} people. Setup and breakdown happen inside your booked time. Overtime is charged at the hourly rate and needs pre-approval.`}
              />
            </div>
            <div style={{ marginTop: 22 }}>
              <Btn href="/book" onClick={go('/book')} variant="fill" wide>Check availability</Btn>
            </div>
          </Card>
        </div>

        <hr className="hddn-rule" style={{ margin: 'var(--gap) 0' }} />
        <p className="hddn-label" style={{ marginBottom: 16 }}>What comes with the room</p>
        <GearBlock sections={studioGear} />

        {studioAddOns.length ? (
          <div style={{ marginTop: 'var(--gap)' }}>
            <AddOns title="Add-ons" items={studioAddOns} />
          </div>
        ) : null}
      </Bay>

      {/* ---- Option 2: Podcast Recording ---- */}
      <Bay band="raised" id="podcast">
        <Head
          eyebrow="Option two"
          title="Podcast Recording"
          lede="A dedicated room with an engineer in it, priced as a package rather than by the hour — you leave with edited episodes and clips, not raw files."
        />
        {podPhotos.length ? (
          <div style={{ marginBottom: 'var(--gap)' }}>
            <PhotoStrip photos={podPhotos} alt="The podcast room at HDDN LAB" ratio="wide" limit={6} />
          </div>
        ) : null}

        {podcastPackages.length ? (
          <div className="hddn-grid hddn-grid--2">
            {podcastPackages.map((p) => (
              <Card key={p.id}>
                <p className="hddn-label">{p.name}</p>
                {p.tagline ? <p className="hddn-h4" style={{ marginTop: 10 }}>{p.tagline}</p> : null}
                {p.description ? <p className="hddn-note" style={{ marginTop: 8 }}>{p.description}</p> : null}
                {p.durations.length ? (
                  <div style={{ marginTop: 18 }}>
                    <Rates
                      caption={`${p.name} rates`}
                      rows={p.durations.map((x) => ({
                        id: `${p.id}-${x.hours}`,
                        label: `${x.hours} hour${x.hours > 1 ? 's' : ''}`,
                        price: x.price,
                      }))}
                    />
                  </div>
                ) : null}
                {p.includes.length ? (
                  <div style={{ marginTop: 20 }}>
                    <p className="hddn-label">Included</p>
                    <div style={{ marginTop: 10 }}><Ticks items={p.includes} /></div>
                  </div>
                ) : null}
                <div style={{ marginTop: 22 }}>
                  <Btn href="/book" onClick={go('/book')} variant="line" wide>Book {p.name}</Btn>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <p className="hddn-lede">
            Podcast packages are being updated. Email <a href={`mailto:${STUDIO.email}`} style={{ color: 'var(--accent-ink)', textDecoration: 'underline' }}>{STUDIO.email}</a> and we will get you a rate.
          </p>
        )}

        <div className="hddn-grid hddn-grid--2" style={{ marginTop: 'var(--gap)' }}>
          <div>
            <p className="hddn-label">What it is good for</p>
            <div style={{ marginTop: 12 }}><Ticks items={uses.podcast} /></div>
            {podcastGear.gear.length ? (
              <div style={{ marginTop: 24 }}>
                <p className="hddn-label">Recording gear</p>
                <div style={{ marginTop: 12 }}><Ticks items={podcastGear.gear} /></div>
              </div>
            ) : null}
            {podcastGear.turnaround ? (
              <p className="hddn-note" style={{ marginTop: 16 }}>{podcastGear.turnaround}</p>
            ) : null}
          </div>
          {podcastAddOns.length ? <AddOns title="Podcast add-ons" items={podcastAddOns} /> : null}
        </div>
      </Bay>

      {/* Something other than a shoot. This is already the answer in FAQ 12. */}
      {/* Deliberately no event rate. Coverage and event work varies too much to
          price on a page, so this names what the room is good for and routes to
          a conversation — which is also what FAQ 12 has always said. */}
      <Bay tight>
        <div className="hddn-grid hddn-grid--sidebar">
          <Head
            eyebrow="Something else"
            title="It does not have to be a shoot."
            lede="Events, workshops, meetings, listening sessions, pop-ups, classes. Tell us what you have in mind and we will work out pricing and logistics."
          >
            <p className="hddn-note" style={{ marginTop: 16, maxWidth: '52ch' }}>
              Want it covered as well as hosted? We shoot events too — that is quoted
              separately, alongside our{' '}
              <a href="/content#one-off" onClick={go('/content#one-off')} style={{ color: 'var(--accent-ink)', textDecoration: 'underline' }}>
                one-off production work
              </a>.
            </p>
          </Head>
          <div style={{ display: 'grid', alignContent: 'end' }}>
            <Btn href="/contact?path=studio" onClick={go('/contact?path=studio')} variant="line" wide>Ask about your idea</Btn>
          </div>
        </div>
      </Bay>

      <Bay band="raised">
        <Head eyebrow="Before you book" title="Good to know." />
        <div className="hddn-grid hddn-grid--2">
          <Defs items={goodToKnow.slice(0, Math.ceil(goodToKnow.length / 2))} />
          <Defs items={goodToKnow.slice(Math.ceil(goodToKnow.length / 2))} />
        </div>
      </Bay>

      <OtherDoor
        k="The other door"
        title="Or hand the whole thing to us."
        body="The same room, the same gear, and a team that shoots and edits your content on a monthly retainer, for local businesses, brands and buildings."
        href="/content"
        cta="See what we make"
      />
    </>
  );
}
